import { useContext, useEffect, useRef, useState } from 'react';
import fileDownload from 'js-file-download';
import {
  Alert,
  Box,
  Button,
  Card,
  CardHeader,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Dayjs } from 'dayjs';

import { grey } from '@mui/material/colors';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import useFileService from '../../../hooks/useFileService';

import DropZone from '../../Report/Input/Dropzone/Dropzone';
import { IFileInfo, Severity, ExpoCombineReport } from '../../../types/reports';
import { SocketContext } from '../../../context/SocketProvider';
import { ISocketContext } from '../../../types/socketclient';
import returnDate from '../../../utils/dateTime';
import { ProgressBar } from '../../../global';
import DebugWindow from '../../Report/DebugWindow/DebugWindow';
import DatePicker from '../../Report/Input/DatePicker/DatePicker';

const ExpoCombine = () => {
  const theme = useTheme();
  const [uploadFile, progressUpdate, downloadFile, getFiles, deleteFile] =
    useFileService();
  const { socket } = useContext(SocketContext) as ISocketContext;

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const clearDropzoneRef = useRef<(() => void) | null>(null);
  const [fileInfos, setFileInfos] = useState<IFileInfo[]>([]);

  const [isAllRequiredFilled, setIsAllRequiredFilled] =
    useState<boolean>(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<Severity>('warning');

  // const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState<Dayjs | null>(null);

  const [pythonOutput, setPythonOutput] = useState<string[]>([]);

  useEffect(() => {
    socket?.on('pythonOutput', (output: string) => {
      console.info('Output from Python', output);
      setPythonOutput((prevOutput) => [...prevOutput, output]);
    });
    socket?.on('uploadComplete', () => {
      const getUploadedFiles = async () => {
        const retrieveFiles = await getFiles();
        if (retrieveFiles.success) {
          setFileInfos(retrieveFiles.data as unknown as IFileInfo[]);
        } else {
          console.error(retrieveFiles.error);
        }
      };
      getUploadedFiles();
    });
    socket?.on('deleteFile', (fileName: string) => {
      setFileInfos((prevFileInfos) =>
        prevFileInfos.filter((file) => file.name !== fileName),
      );
    });
    return () => {
      socket?.off('pythonOutput');
    };
  }, [getFiles, socket]);

  useEffect(() => {
    const getUploadedFiles = async () => {
      const retrieveFiles = await getFiles();
      if (retrieveFiles.success) {
        setFileInfos(retrieveFiles.data as unknown as IFileInfo[]);
      } else {
        console.error(retrieveFiles.error);
      }
    };
    getUploadedFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedFiles.length && invoiceDate !== null) {
      setIsAllRequiredFilled(true);
    } else {
      setIsAllRequiredFilled(false);
    }
  }, [selectedFiles, invoiceDate]);

  const handleFileChange = (files: File[]) => {
    setSelectedFiles(files);
  };

  const handleDateChange = (date: Dayjs) => {
    setInvoiceDate(date);
  };

  const clearFiles = () => {
    if (clearDropzoneRef.current) {
      clearDropzoneRef.current();
    }
  };

  const upload = () => {
    const filesForUpload: ExpoCombineReport = {
      files: selectedFiles,
      invoiceDate,
    };
    setSeverity('info');
    setMessage('Analyzing data...');
    if (selectedFiles.length > 0) {
      uploadFile(filesForUpload).then((result) => {
        if (result.success) {
          setMessage(result.message || '');
          setSeverity('success');
        } else {
          setMessage(result.error || 'Could not upload the file!');

          setSeverity('error');
        }
        setSelectedFiles([]);
        clearFiles();
      });
    } else {
      setMessage('No file to analyze!');
      setSeverity('error');
    }
  };

  return (
    <>
      <Box
        sx={{
          marginBottom: '1em',
          marginTop: '1em',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-evenly',
        }}
      >
        {/* Commenting out per Spaon's request 11/16/23 */}
        {/* <TextField
          sx={{ ml: theme.spacing(1) }}
          label="Invoice Number:"
          id="invoiceNumber"
          value={invoiceNumber}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setInvoiceNumber(e.target.value);
          }}
          fullWidth
        /> */}
        <DatePicker
          label="Invoice Date"
          selectedDate={invoiceDate}
          onDateChange={handleDateChange}
        />
      </Box>

      {progressUpdate > 0 && (
        <Box sx={{ width: '100%' }}>
          <ProgressBar value={progressUpdate} />
        </Box>
      )}

      <DropZone
        selectedFiles={selectedFiles}
        onFileChange={handleFileChange}
        clearFiles={(clearFunc: () => void) => {
          clearDropzoneRef.current = clearFunc;
        }}
      />
      <DebugWindow data={pythonOutput} />

      <Box sx={{ textAlign: 'center', marginBottom: theme.spacing(3) }}>
        <Button
          variant="contained"
          color="success"
          disabled={!isAllRequiredFilled}
          onClick={upload}
          sx={{
            '&.Mui-disabled': {
              background: '#7bb274',
              color: '#fff',
            },
          }}
        >
          Upload
        </Button>
      </Box>

      {message.length > 0 && (
        <Stack sx={{ mb: theme.spacing(2) }} spacing={2}>
          <Alert
            severity={severity}
            onClose={() => {
              setMessage('');
            }}
          >
            {message}
          </Alert>
        </Stack>
      )}

      {fileInfos && fileInfos.length > 0 && (
        <Card variant="outlined">
          <CardHeader
            title="List of Files"
            sx={{ bgcolor: grey[50], borderBottom: `1px solid ${grey[200]}` }}
          />
          <List>
            {fileInfos.map((file) => (
              <Box
                sx={{ borderTop: `1px solid ${grey[200]}` }}
                key={`${file.name}-${file.date}`}
              >
                <ListItem
                  component="div"
                  sx={{ justifyContent: 'space-between' }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      alignSelf: 'start',
                      width: '50%',
                      wordWrap: 'break-word',
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Box>
                    <Button
                      variant="contained"
                      startIcon={<FileDownloadIcon />}
                      color="black"
                      size="small"
                      sx={{ mr: theme.spacing(1) }}
                      onClick={async () => {
                        const fileToDownload = await downloadFile(file.name);
                        if (fileToDownload.success) {
                          fileDownload(
                            fileToDownload.data as unknown as File,
                            file.name,
                          );
                        } else {
                          console.error(fileToDownload.error);
                        }
                      }}
                    >
                      Download
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<DeleteIcon />}
                      color="error"
                      size="small"
                      onClick={async () => {
                        const fileDeleted = await deleteFile(file.name);
                        if (!fileDeleted.success) {
                          console.error(fileDeleted.error);
                        }
                      }}
                    >
                      Delete
                    </Button>
                    <Typography variant="body2" sx={{ textAlign: 'end' }}>
                      {returnDate(file.date)}
                    </Typography>
                  </Box>
                </ListItem>
              </Box>
            ))}
          </List>
        </Card>
      )}
    </>
  );
};

export default ExpoCombine;
